import { useParams } from 'react-router-dom'
import { usePlan } from '../../api/queries'
import type { PrefetchOutcome } from '../../offline/prefetch'
import { czechPlural } from '../../shared/czech'
import { useOnline } from '../../shared/hooks'
import { usePrefetch } from './usePrefetch'
import { useStartSession } from './useStartSession'

/**
 * The prefetch trigger point (§5.1).
 *
 * Prefetch starts on **screen entry**, not on the Start tap. Start stays
 * disabled until the payload is complete, and an incomplete prefetch produces
 * an explicit, actionable state — never a session that begins and then runs out
 * of items. On a flaky network at session start, this screen is where that
 * guarantee holds or breaks.
 */
export function DrillScreen() {
  const { descriptorId = '' } = useParams()
  const online = useOnline()
  const { state, retry } = usePrefetch(descriptorId)
  const { start, starting, error: startError } = useStartSession(descriptorId)

  const plan = usePlan()
  const descriptor = plan.data?.descriptors.find((d) => d.id === descriptorId)

  return (
    <div className="stack">
      <h1>{descriptor?.title ?? 'Procvičování'}</h1>
      {descriptor ? <p className="muted">{descriptor.subtitle}</p> : null}

      {state.phase === 'prefetching' ? (
        <div className="card">
          <p>Stahuji úlohy…</p>
          <p className="muted small">
            {state.attempt > 0 ? `Pokus ${state.attempt + 1}. ` : null}
            Úlohy se stahují hned při vstupu na obrazovku, aby spuštění nezáviselo na síti.
          </p>
        </div>
      ) : null}

      {state.phase === 'unavailable' ? (
        <div className="card card--warn">
          <h2 className="card__title">Úlohy se nepodařilo stáhnout</h2>
          <p className="muted">
            {online
              ? 'Server neodpověděl ani po několika pokusech.'
              : 'Jste offline a tyto úlohy zatím nejsou stažené.'}
          </p>
          <p className="small mono">{state.error}</p>
          <button className="button button--primary" type="button" onClick={retry}>
            Zkusit znovu
          </button>
        </div>
      ) : null}

      {state.phase === 'ready' ? (
        <div className="card">
          <p>
            Připraveno <strong>{state.outcome.cachedCount}</strong> úloh v offline úložišti.
          </p>

          {state.outcome.skipped.length > 0 ? <SkippedNote skipped={state.outcome.skipped} /> : null}

          <button
            className="button button--primary"
            type="button"
            disabled={starting || state.outcome.cachedCount === 0}
            onClick={() => void start(state.outcome.preparation)}
          >
            {starting ? 'Spouštím…' : 'Začít'}
          </button>

          {startError ? <p className="error">{startError}</p> : null}
        </div>
      ) : null}
    </div>
  )
}

/** §7.4: items this client cannot render are skipped visibly, never silently. */
function SkippedNote({ skipped }: { skipped: PrefetchOutcome['skipped'] }) {
  const types = [...new Set(skipped.map((s) => s.type))].join(', ')
  return (
    <p className="muted small">
      Přeskočeno {skipped.length} {czechPlural(skipped.length, ['úlohu', 'úlohy', 'úloh'])}, které tato verze
      aplikace neumí zobrazit ({types}). Sezení kvůli tomu neselže — viz specifikace §7.4.
    </p>
  )
}
